import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react';
import db_config from '../../configs/db_config';
import { TABS } from '../../consts';
import { CategoriesType, FoodDetailItem, MenuContextValue, RestaurantWithFoodType, listRestaurantType } from '../../interfaces/types';
import { db } from '../../libs/firebase';
import { useSystemContext } from '../SystemContext/SystemContext';

const getStadiumManageFoodDetails = () => {
  return db.collection(db_config.DB_TABLE_ALL_FOOD_DETAILS);
};

const getManageAllCategories = () => {
  return db.collection(db_config.DB_TABLE_ALL_CATEGORIES);
};

const getRestaurantConfig = () => {
  return db.collection(db_config.DB_TABLE_RESTAURANT_CONFIG);
};

const MenuContext = createContext<MenuContextValue>({
  listRestaurantData: [],
  listRestaurantWithFood: [],
  loading: true,
  loadingFoods: true,
  findRestaurantById: () => {},
  setFetchData: () => {},
  setListRestaurantWithFood: () => {},
  activeTab: TABS.HOME,
  setActiveTab: () => {},
  restaurantFoodMap: {},
});

const MenuProvider = ({ children }: { children: ReactNode }) => {
  const [activeTab, setActiveTab] = useState(TABS.HOME);
  const [loading, setLoading] = useState(true);
  const [loadingFoods, setLoadingFoods] = useState(true);
  const [fetchData, setFetchData] = useState(false);
  const { stadiumInfo } = useSystemContext();

  const [listRestaurantData, setListRestaurantData] = useState<listRestaurantType[]>([]);
  const [listRestaurantWithFood, setListRestaurantWithFood] = useState<RestaurantWithFoodType[]>([]);
  const [restaurantFoodMap, setRestaurantFoodMap] = useState<{ [key: string]: FoodDetailItem[] }>({});

  const fetchRestaurantData = useCallback(() => {
    try {
      setLoading(true);
      if (!stadiumInfo?.id) {
        return;
      }

      const unsubscribeCategories = getManageAllCategories().onSnapshot((querycategories) => {
        const categoryDocs: CategoriesType[] = querycategories.docs.map((doc) => ({
          id: doc.id,
          category_show: doc.data().category_show,
          foooIds: doc.data().foodIds,
          order: doc.data().order,
          restaurantId: doc.data().restaurantId,
          title: doc.data().title,
        }));

        const unsubscribeRestaurant = getRestaurantConfig()
          .where('stadium_id', '==', stadiumInfo.id)
          .where('status', '==', 'active')
          .onSnapshot(async (queryRestaurantConfig) => {
            const listRestaurant = await Promise.all(
              queryRestaurantConfig.docs.map(async (doc) => {
                const data = doc.data();

                const restaurant: listRestaurantType = {
                  id: doc.id,
                  name: data.name,
                  region: data.region,
                  location_id: data.location_id ?? '',
                  merchant_id: data.merchant_id ?? '',
                  restaurant_profile_pic: data.restaurant_profile_pic,
                  categories: categoryDocs.filter((category) => category.category_show === 1 && category.restaurantId === doc.id),
                  opening_schedule: data.opening_schedule,
                  isOnline: data.isOnline,
                  restaurant_open_id: data.restaurant_open_id,
                  restaurant_open_state: data.restaurant_open_state,
                  isStadiumPromotion: stadiumInfo.isStadiumPromotion ?? false,
                  promotion_restaurant_id: stadiumInfo.promotion_restaurant_id ?? '',
                  sold: data.sold ?? 0,
                };

                return restaurant;
              })
            );

            listRestaurant.sort((a, b) => b.sold - a.sold);
            if (listRestaurant.length === 0) {
              await addLogs('list restaurants empty');
            }
            setListRestaurantData(listRestaurant);
            setLoading(false);
          });

        return () => {
          unsubscribeRestaurant && unsubscribeRestaurant();
        };
      });

      return () => {
        unsubscribeCategories && unsubscribeCategories();
      };
    } catch (error) {
      setLoading(false);
    }
  }, [stadiumInfo?.id]);

  const fetchFoodsData = useCallback(() => {
    setLoadingFoods(true);
    if (listRestaurantData.length > 0) {
      const restaurantFoodMap: Record<string, FoodDetailItem[]> = {};
      const addedFoodIds: Record<string, boolean> = {};

      listRestaurantData.forEach((restaurant) => {
        const categoryIds = restaurant.categories.map((categoryDoc) => categoryDoc.id);
        const foodChunks = chunkArray(categoryIds, 20);

        foodChunks.forEach((chunk) => {
          getStadiumManageFoodDetails()
            .where('restaurantId', '==', restaurant.id)
            .where('categories', 'array-contains-any', chunk)
            .orderBy('create_time', 'desc')
            .onSnapshot(
              (querySnapshot) => {
                querySnapshot.docChanges().forEach((change) => {
                  const foodData = change.doc.data();
                  const foodDetail: FoodDetailItem = {
                    enabled: foodData.enabled,
                    enabled_note: foodData.enabled_note,
                    isEnableStockCount: foodData.isEnableStockCount,
                    description: foodData.description,
                    food_show_status: foodData.food_show_status,
                    unit_price: foodData.unit_price,
                    restaurantId: foodData.restaurantId,
                    customize_group_settings: foodData.customize_group_settings,
                    id: foodData.id,
                    price: foodData.price,
                    image_url: foodData.image_url,
                    name: foodData.name,
                    food_id: foodData.food_id,
                    categories: foodData.categories,
                    sku: foodData.sku,
                    tax_rate: foodData.tax_rate,
                    food_type: foodData.food_type,
                    sold: foodData.sold ?? 0,
                  };

                  switch (change.type) {
                    case 'added':
                      if (!addedFoodIds[foodDetail.id]) {
                        if (!restaurantFoodMap[foodDetail.restaurantId]) {
                          restaurantFoodMap[foodDetail.restaurantId] = [];
                        }
                        restaurantFoodMap[foodDetail.restaurantId].push(foodDetail);
                        addedFoodIds[foodDetail.id] = true;
                      }
                      break;
                    case 'modified':
                      if (restaurantFoodMap[foodDetail.restaurantId]) {
                        restaurantFoodMap[foodDetail.restaurantId] = restaurantFoodMap[foodDetail.restaurantId].map((item) =>
                          item.id === foodDetail.id ? foodDetail : item
                        );
                      }
                      break;
                    case 'removed':
                      if (restaurantFoodMap[foodDetail.restaurantId]) {
                        restaurantFoodMap[foodDetail.restaurantId] = restaurantFoodMap[foodDetail.restaurantId].filter((item) => item.id !== foodDetail.id);
                        delete addedFoodIds[foodDetail.id];
                      }
                      break;
                    default:
                      break;
                  }
                });
                setRestaurantFoodMap({ ...restaurantFoodMap });
              },
              (error) => {
                console.error('Error fetching food snapshot:', error);
              }
            );
        });
      });
    }
  }, [listRestaurantData]);

  function chunkArray(categories: any, size: number) {
    const chunkedArr = [];
    let index = 0;
    while (index < categories.length) {
      chunkedArr.push(categories.slice(index, size + index));
      index += size;
    }
    return chunkedArr;
  }

  const addLogs = async (error: string) => {
    try {
      const docRef = db.collection('logs').doc('error');
      const errorEntry = {
        message: error,
        timestamp: new Date().toISOString(),
      };

      await docRef.set(
        {
          data: firebase.firestore.FieldValue.arrayUnion(errorEntry),
        },
        { merge: true }
      );
    } catch (err) {
      console.error('error:', err);
    }
  };

  const mergeRestaurantData = async () => {
    const updatedListRestaurantData = await Promise.all(
      listRestaurantData.map(async (restaurant) => {
        if (restaurant.id in restaurantFoodMap) {
          const filteredFoods = restaurantFoodMap[restaurant.id].filter((food) =>
            food.categories.some((foodCategory) => restaurant.categories.some((restaurantCategory) => restaurantCategory.id === foodCategory))
          );

          const updatedFoodDetails = filteredFoods.map((food) => ({
            ...food,
            sold: food.sold ?? 0,
          }));

          return { ...restaurant, food_details: updatedFoodDetails };
        } else {
          return { ...restaurant, food_details: [] };
        }
      })
    );

    setListRestaurantWithFood(updatedListRestaurantData);
    setLoadingFoods(false);
  };

  const findRestaurantById = (id: string): RestaurantWithFoodType | undefined => {
    const restaurant = listRestaurantWithFood.find((restaurant) => restaurant.id === id);
    return restaurant ? restaurant : undefined;
  };

  useEffect(() => {
    fetchRestaurantData();
  }, [stadiumInfo?.id, fetchData]);

  useEffect(() => {
    fetchFoodsData();
  }, [listRestaurantData]);

  useEffect(() => {
    const fetchData = async () => {
      await mergeRestaurantData();
    };

    fetchData();
  }, [restaurantFoodMap]);

  return (
    <MenuContext.Provider
      value={{
        loading,
        loadingFoods,
        listRestaurantData,
        listRestaurantWithFood,
        findRestaurantById,
        setFetchData,
        setListRestaurantWithFood,
        activeTab,
        setActiveTab,
        restaurantFoodMap,
      }}
    >
      {children}
    </MenuContext.Provider>
  );
};

const useMenuContext = () => {
  const context = useContext(MenuContext);
  return context;
};

export { MenuProvider, useMenuContext };

